import { Box, Button, Card, CardMedia, Typography } from '@mui/material'
import React from 'react'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Product, getImageUrlBySize } from 'src/Libs/shop'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { QuantityAvailabilityIndicator } from '../../ShopProduct'
import { ShopDownloadButton } from '../../Button'

interface TransformerCardProps {
  productData: Product
  selectedProductId: string | null
  onSelectedPrductIdChange: (productId: string | null) => void
  onMoreInfoProduct: (productId: string) => void
  showPrice?: boolean
}

interface StyleProps {
  isProductSelected: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  button: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.only('xs')]: {
      margin: theme.spacing(1),
    },
  },
  productImage: {
    margin: 0,
    padding: 0,
    objectFit: 'contain',
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    marginBottom: '12px',
  },
  product: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '285px',
    maxWidth: '285px',
    flexShrink: 0,
    margin: '12px',
    padding: 0,
    paddingBottom: '12px',
    border: props.isProductSelected ? `1px solid ${theme.palette.primary.main}` : 'none',
  },
  containerTextWrapper: {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  priceContainer: {
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  flexPrice: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  modalButtonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  downloadButtonContainer: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}))

const TransformerCard: React.FC<TransformerCardProps> = ({
  productData,
  selectedProductId,
  showPrice,
  onSelectedPrductIdChange,
  onMoreInfoProduct,
}) => {
  const { t } = useCustomTranslation()
  const isProductSelected = productData.id === selectedProductId
  const { classes } = useStyles({ isProductSelected })
  const unitPrice = productData?.calculatedPrice.unitPrice
  const price = productData?.calculatedPrice.listPrice?.price ?? 0
  const productImageUrl = getImageUrlBySize(productData?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
  const productAvailability = productData.extensions?.e3dcAvailability
  const productContractDetails = productData.extensions.blankContract
  const isPriceApplied = Boolean(productData.extensions?.e3dcPriceApplied?.state)

  return (
    <Card key={productData.id} className={classes.product}>
      <Box sx={{ flex: '1' }}>
        {productContractDetails && (
          <Box className={classes.downloadButtonContainer}>
            <ShopDownloadButton contractDetails={productContractDetails} />
          </Box>
        )}
        <CardMedia className={classes.productImage} component="img" image={productImageUrl} alt="Product" />
        <Box className={classes.containerTextWrapper}>
          <Typography align="left" variant="h3">
            {productData.translated && productData.translated.name}
            <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
          </Typography>
          {showPrice && (
            <Box className={classes.priceContainer}>
              <Box className={classes.flexPrice}>
                <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
                  {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
                </Typography>
                {unitPrice < price && (
                  <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                    {currencyFormatter(price)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Box className={classes.modalButtonContainer}>
        <Button
          className={classes.button}
          size="large"
          color="inherit"
          variant="outlined"
          fullWidth
          onClick={() => {
            onMoreInfoProduct(productData.id)
          }}
        >
          {t('shop.common.more-info')}
        </Button>
        {productData.id !== selectedProductId ? (
          <Button
            className={classes.button}
            color="primary"
            variant="contained"
            fullWidth
            onClick={() => onSelectedPrductIdChange(productData.id)}
            disabled={!isPriceApplied}
          >
            {t('shop.common.select')}
          </Button>
        ) : (
          <Button
            className={classes.button}
            color="inherit"
            variant="contained"
            fullWidth
            onClick={() => onSelectedPrductIdChange(null)}
            disabled={!isPriceApplied}
          >
            {t('shop.common.select')}
          </Button>
        )}
      </Box>
    </Card>
  )
}

export default TransformerCard
